import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import ProcessSteps from "@/components/ProcessSteps";
import CTASection from "@/components/CTASection";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import ServiceCard from "@/components/ServiceCard";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Remodeling" }];

export default function Remodeling() {
  return (
    <>
      <SEO
        title="Remodeling Contractor Austin TX | Black Ridge Construction"
        description="Kitchen, bathroom, and full-home remodels in Austin and Central Texas. Practical layouts, durable finishes, and one general contractor coordinating every trade."
      />

      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-16">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Remodeling</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Practical, Durable Remodels for Austin Homes</h1>
            <p className="mt-4 text-gray-300">From kitchen refreshes to full-home gut remodels, Black Ridge Construction coordinates every trade under one general contractor. We focus on layouts that actually work, finishes that hold up, and the kind of communication you should expect from a professional GC.</p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <ServiceCard eyebrow="Remodel" title="Kitchen Remodeling" description="Layouts, cabinets, counters, lighting, and finishes that work for real cooking." to="/services/kitchen-remodeling" image="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=900&q=80" />
          <ServiceCard eyebrow="Remodel" title="Bathroom Remodeling" description="Walk-in showers, tile, plumbing, ventilation, and aging-in-place updates." to="/services/bathroom-remodeling" image="https://images.unsplash.com/photo-1620626011761-996317b8d101?w=900&q=80" />
          <ServiceCard eyebrow="Remodel" title="Full Home Remodel" description="Whole-house remodels coordinated end-to-end by one GC." to="/services/full-home-remodeling" image="https://images.unsplash.com/photo-1503174971373-b1f69850bded?w=900&q=80" />
          <ServiceCard eyebrow="Remodel" title="Flooring Installation" description="Hardwood, LVP, tile, and prep work across the home." to="/services/flooring-installation" image="https://images.unsplash.com/photo-1622372738946-62e02505feb3?w=900&q=80" />
          <ServiceCard eyebrow="Remodel" title="Interior Painting" description="Clean prep and professional finishes for repaints." to="/services/interior-painting" image="https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=900&q=80" />
          <ServiceCard eyebrow="Remodel" title="Trim Carpentry" description="Crown, base, casing, built-ins, and finish detail." to="/services/trim-carpentry" image="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=900&q=80" />
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-br grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="br-eyebrow">What homeowners typically want</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Practical Upgrades. Better Layouts. Higher Resale Value.</h2>
            <p className="mt-3 text-gray-700">Austin homes are changing fast. Older homes need updated layouts. Newer homes need finish upgrades. Investor properties need make-readies. Aging homeowners need accessibility modifications. We approach each remodel honestly — starting with what the home is, how you use it, and what makes the most sense to change.</p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
              {["Kitchen remodels","Bathroom remodels","Full home remodels","Interior updates","Flooring & paint","Drywall & trim","Layout changes","Investor improvements","Make-ready updates","Aging-in-place modifications","Modern home upgrades"].map(t => (
                <li key={t} className="flex gap-2"><span className="text-br-bronze">▪</span>{t}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-br-paper rounded p-6 border border-gray-200">
              <ContactForm title="Start Your Remodel" source_page="remodeling" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Our Process</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">From Concept to Final Walk-Through</h2>
          </div>
          <ProcessSteps />
        </div>
      </section>

      <CTASection title="Ready to Start Your Remodel?" subtitle="We will walk through the home with you, listen to how you use it, and give honest options at multiple price levels." />
    </>
  );
}
